import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';

function lim(v) {
  return v != null ? String(v) : 'Unlimited';
}

const LIMIT_KEYS = [
  ['request_limit_per_minute', 'Requests / minute'],
  ['daily_request_limit', 'Daily requests'],
  ['monthly_request_limit', 'Monthly requests'],
  ['concurrent_request_limit', 'Concurrent requests'],
  ['burst_limit', 'Burst']
];

export default function TenantUsagePolicyPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [form, setForm] = useState({
    policy_name: 'Tenant quota override',
    request_limit_per_minute: '',
    daily_request_limit: '',
    monthly_request_limit: '',
    concurrent_request_limit: '',
    burst_limit: ''
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch('/api/tenant/usage-policy/effective');
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
      const o = json.sources?.tenantOverride;
      if (o) {
        setForm({
          policy_name: o.policy_name || 'Tenant quota override',
          request_limit_per_minute: o.request_limit_per_minute != null ? String(o.request_limit_per_minute) : '',
          daily_request_limit: o.daily_request_limit != null ? String(o.daily_request_limit) : '',
          monthly_request_limit: o.monthly_request_limit != null ? String(o.monthly_request_limit) : '',
          concurrent_request_limit: o.concurrent_request_limit != null ? String(o.concurrent_request_limit) : '',
          burst_limit: o.burst_limit != null ? String(o.burst_limit) : ''
        });
      }
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const numOrEmpty = (s) => {
    const t = String(s || '').trim();
    return t === '' ? null : Number(t);
  };

  const saveOverride = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        policy_name: form.policy_name.trim() || 'Tenant quota override',
        request_limit_per_minute: numOrEmpty(form.request_limit_per_minute),
        daily_request_limit: numOrEmpty(form.daily_request_limit),
        monthly_request_limit: numOrEmpty(form.monthly_request_limit),
        concurrent_request_limit: numOrEmpty(form.concurrent_request_limit),
        burst_limit: numOrEmpty(form.burst_limit)
      };
      const res = await tenantApiFetch('/api/tenant/usage-policy/override', { method: 'PUT', body });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    setSaving(true);
    setError(null);
    setConfirmClear(false);
    try {
      const res = await tenantApiFetch('/api/tenant/usage-policy/override', { method: 'DELETE', body: {} });
      const json = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 404) throw new Error(json.error || 'Remove failed');
      setForm({
        policy_name: 'Tenant quota override',
        request_limit_per_minute: '',
        daily_request_limit: '',
        monthly_request_limit: '',
        concurrent_request_limit: '',
        burst_limit: ''
      });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) return null;

  const eff = data.effective || {};
  const src = data.sources || {};

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        API usage policy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Effective quotas combine platform defaults, your plan policy, and an optional tenant override. Daily/monthly
        figures align with UTC usage rollups (
        <code>usage_daily</code>
        ). Per-minute limits require gateway or app enforcement.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Effective limits
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Chip size="small" label={`Resolution: ${data.resolution}`} sx={{ mr: 1 }} />
          <Chip size="small" label={`Enforcement: ${eff.enforcement_mode || '—'}`} />
        </Box>
        <Grid container spacing={2}>
          {LIMIT_KEYS.map(([key, label]) => (
            <Grid item xs={12} sm={6} key={key}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body1">{lim(eff[key])}</Typography>
            </Grid>
          ))}
        </Grid>
        {data.legacy?.plan_api_call_limit_as_daily != null && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Plan <code>api_call_limit</code> ({data.legacy.plan_api_call_limit_as_daily}) contributes to daily cap when
            no policy sets daily explicitly.
          </Alert>
        )}
        {data.telemetry?.note && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {data.telemetry.note}
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Sources
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          Tenant override
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {src.tenantOverride ? src.tenantOverride.policy_name : 'None'}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          Plan policy
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {src.planPolicy ? src.planPolicy.policy_name : 'None'}
          {data.tenant?.subscribed_plan_name ? ` · Plan: ${data.tenant.subscribed_plan_name}` : ''}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          Platform default
        </Typography>
        <Typography variant="body2">{src.platformDefault ? src.platformDefault.policy_name : 'None'}</Typography>
      </Paper>

      <TenantCan permission="tenant.usage_policies.override">
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Tenant quota override
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Overrides cannot exceed inherited caps from plan and platform. Leave fields empty for unlimited (inherit)
            for that dimension.
          </Typography>
          <TextField
            label="Override label"
            fullWidth
            margin="normal"
            size="small"
            value={form.policy_name}
            onChange={(e) => set('policy_name', e.target.value)}
          />
          {LIMIT_KEYS.map(([key, label]) => (
            <TextField
              key={key}
              label={label}
              fullWidth
              margin="dense"
              size="small"
              value={form[key]}
              onChange={(e) => set(key, e.target.value)}
            />
          ))}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button variant="contained" onClick={saveOverride} disabled={saving}>
              {saving ? <CircularProgress size={22} /> : 'Save override'}
            </Button>
            {src.tenantOverride && (
              <Button color="error" variant="outlined" onClick={() => setConfirmClear(true)} disabled={saving}>
                Remove override
              </Button>
            )}
          </Box>
        </Paper>
      </TenantCan>

      <Dialog open={confirmClear} onClose={() => !saving && setConfirmClear(false)}>
        <DialogTitle>Remove tenant override?</DialogTitle>
        <DialogContent>
          <DialogContentText>Effective policy will fall back to plan and platform defaults only.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClear(false)} disabled={saving}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={clearOverride} disabled={saving}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
